// Attendance.tsx
import { Fragment, useCallback, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Calendar,
  Download,
  Eye,
  User,
  Clock,
  CheckCircle,
  XCircle,
  TrendingUp,
  Users,
  Activity,
  Filter,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronUp,
  Briefcase,
  Menu
} from "lucide-react";
import {
  getAttendanceDayWise,
  getAttendanceSummary,
  getAttendanceStatus,
  exportAttendanceToCSV,
  adminMarkPresent,
  adminMarkAbsent,
  adminUpdateWorkingHours,
  sendWebClockEvent,
  getMyWorkMode,
  getAttendanceByDate,
  AttendanceWorkingHour,
  AttendanceStatus,
  AttendanceSession,
  TodayAttendance,
  PieData
} from "../api/attendance";
import { LogIn, LogOut, MapPin } from "lucide-react";
import { getOffice } from "../api/officeandcompany";
import { canAccessManagerPanel } from "../utils/rolePermissions";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/useToast";

// A punch is only "real" when it has an in- or out-time. The backend can
// return a trailing empty session (both null) for an open / partially-filled
// day; those must never be counted as punches or rendered as a blank row.
const realSessions = (arr?: AttendanceSession[] | null): AttendanceSession[] =>
  (arr || []).filter((s) => !!(s.in_time || s.out_time));

// The out-time to display for a day. Prefer the top-level out_time, but fall
// back to the MOST RECENT punch that actually has a clock-out. The backend
// sets the top-level out_time to null whenever the day ends on an open
// session (still clocked in) — e.g. an office 11:52 → 16:11 punch followed by
// an open 22:12 clock-in. In that case the day's real out-time is still the
// last completed clock-out (16:11), and it should show instead of a blank.
// This also covers the cross-midnight overtime case where the settled
// out-time lives only in `sessions`.
const settledOutTime = (record: AttendanceWorkingHour): string | null => {
  if (record.out_time && record.out_time !== "00:00:00") return record.out_time;
  const punches = realSessions(record.sessions);
  for (let i = punches.length - 1; i >= 0; i--) {
    const out = punches[i].out_time;
    if (out && out !== "00:00:00") return out;
  }
  return null;
};

// Today's date in the user's LOCAL timezone as YYYY-MM-DD. Do NOT use
// `new Date().toISOString()` here — that's UTC, so in IST (UTC+5:30) it returns
// yesterday between midnight and 5:30 AM, which froze the attendance end-date on
// the previous day (and fetched stale data) just after midnight.
const localToday = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const Attendance = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  // All manager-tier + elevated roles can override attendance. The
  // backend (`hrms/routers/attendance.py::_authorize_attendance_change`)
  // mirrors this set: elevated roles (admin / owner / superadmin /
  // founder / director / hr head) get branch-wide authority; manager-
  // tier (manager / team lead / hod / hr) are scoped to their direct
  // reports. Supervisor and employee remain excluded.
  const canOverrideAttendance = canAccessManagerPanel(user?.role);
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  // Intern is the employee tier — same self-service attendance view.
  const isEmployee = ["employee", "intern"].includes((user?.role || "").toLowerCase());
  const [myMonthHours, setMyMonthHours] = useState<AttendanceWorkingHour[]>([]);
  const [myMonthLoading, setMyMonthLoading] = useState(false);
  const [pieData, setPieData] = useState<PieData[]>([]);
  const [todayAttendance, setTodayAttendance] = useState<TodayAttendance[]>([]);
  const [employeeStatus, setEmployeeStatus] = useState<AttendanceStatus[]>([]);
  const [filteredEmployeeStatus, setFilteredEmployeeStatus] = useState<AttendanceStatus[]>([]);
  const todayStr = localToday();
  const monthStartStr = todayStr.slice(0, 8) + "01"; // YYYY-MM-01
  const [startDate, setStartDate] = useState<string>(monthStartStr);
  const [endDate, setEndDate] = useState<string>(todayStr);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "Present" | "Absent" | "Leave">("all");
  // Draft filter values bound to the inputs. They only get copied into the
  // applied state (above) when the user clicks Apply, so typing / picking
  // a date doesn't refetch or refilter until they confirm.
  const [draftStartDate, setDraftStartDate] = useState<string>(monthStartStr);
  const [draftEndDate, setDraftEndDate] = useState<string>(todayStr);
  const [draftSearchQuery, setDraftSearchQuery] = useState("");
  const [draftStatusFilter, setDraftStatusFilter] = useState<"all" | "Present" | "Absent" | "Leave">("all");
  const [showInfoPopup, setShowInfoPopup] = useState(false);

  // (filter collapsible state removed — filters are always visible now)
  const [isStatusTableOpen, setIsStatusTableOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // True when the current user's Employee.modeOfWorking is remote or hybrid
  // (or the legacy workfromhome boolean is set). Resolved from the backend
  // on mount via /attendance/my-mode so it's always fresh — even if the
  // localStorage value the JWT-based login wrote is stale or missing.
  // While the request is in flight we leave it false; the clock function
  // still has its own localStorage fallback, so the worst case is the
  // user gets the existing geofenced flow during the first second after
  // page load.
  const [myWorkfromhome, setMyWorkfromhome] = useState(false);
  // Raw Employee.modeOfWorking for the logged-in user. We gate the
  // unlimited clock-in/out behavior on this being exactly "remote" —
  // hybrid / in-office keep the original one-cycle-per-day flow untouched.
  const [myModeOfWorking, setMyModeOfWorking] = useState<string | null>(null);
  // Overtime: live flag + clock-button cutoff (ISO local). When on overtime,
  // the employee gets the SAME unlimited clock-in/out button remote gets,
  // until `myOvertimeUntil` (next working day office start - 30 min).
  const [myIsOvertime, setMyIsOvertime] = useState(false);
  const [myOvertimeUntil, setMyOvertimeUntil] = useState<string | null>(null);
  // Overtime button LOWER bound = work day's office end. The button is only
  // offered AFTER office hours, so it stays hidden until now >= this.
  const [myOvertimeFrom, setMyOvertimeFrom] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMyWorkMode()
      .then((m) => {
        if (cancelled) return;
        setMyWorkfromhome(!!m.workfromhome);
        setMyModeOfWorking(m.mode_of_working);
        setMyIsOvertime(!!m.is_overtime);
        setMyOvertimeUntil(m.overtime_clock_until ?? null);
        setMyOvertimeFrom(m.overtime_clock_from ?? null);
      })
      .catch(() => { /* silent — sendWebClockEvent has its own fallbacks */ });
    return () => { cancelled = true; };
  }, []);

  // Remote employees may clock in/out any number of times a day; the
  // button below toggles on live session state instead of the once-only
  // first_in / last_out gate the in-office flow uses.
  const isRemote = (myModeOfWorking || "").trim().toLowerCase() === "remote";

  // Re-evaluated every 30s so the overtime button auto-hides at the cutoff.
  const [nowTick, setNowTick] = useState<number>(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  const isOvertimeActive = !!(
    myIsOvertime &&
    myOvertimeUntil &&
    // Overtime button only AFTER office hours: now must be past the "from"
    // (office end). When no "from" is configured, fall back to until-only.
    (!myOvertimeFrom || nowTick >= new Date(myOvertimeFrom).getTime()) &&
    nowTick < new Date(myOvertimeUntil).getTime()
  );
  // Remote and overtime share the same unlimited clock-in/out UI + live state.
  const showSessionClock = isRemote || isOvertimeActive;

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<TodayAttendance | null>(null);

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedEmployeeStatus, setSelectedEmployeeStatus] = useState<AttendanceStatus | null>(null);

  // Remote: per-day punch breakdown popup (opened by clicking a day row).
  const [sessionsDialogOpen, setSessionsDialogOpen] = useState(false);
  const [sessionsRecord, setSessionsRecord] = useState<AttendanceWorkingHour | null>(null);
  
  // State for attendance working hours data
  const [attendanceWorkingHours, setAttendanceWorkingHours] = useState<AttendanceWorkingHour[]>([]);
  const [isLoadingWorkingHours, setIsLoadingWorkingHours] = useState(false);
  const [workingHoursError, setWorkingHoursError] = useState<string | null>(null);
  const [hoursModalOpen, setHoursModalOpen] = useState(false);
  const [hoursModalMode, setHoursModalMode] = useState<'mark-present' | 'edit'>('edit');
  const [hoursTarget, setHoursTarget] = useState<AttendanceStatus | null>(null);
  const [hoursInValue, setHoursInValue] = useState("");
  // Optional clock-out — admins can save attendance with only an in-time
  // and add the out-time later, or backfill both for a past date.
  const [hoursOutValue, setHoursOutValue] = useState("");
  const [hoursDateValue, setHoursDateValue] = useState(
    localToday(),
  );
  const [hoursSubmitting, setHoursSubmitting] = useState(false);
  const [markingPresentFor, setMarkingPresentFor] = useState<string | null>(null);
  const [markingAbsentFor, setMarkingAbsentFor] = useState<string | null>(null);
  const [pendingMarkPresent, setPendingMarkPresent] = useState<Set<string>>(
    new Set(),
  );
  const [webClockBusy, setWebClockBusy] = useState<"in" | "out" | null>(null);
  // Office-wide shift duration ("HH:MM") computed from the office's
  // working_hours_start / working_hours_end. Used as the fallback when
  // the day-wise API returns no shift info — e.g. an employee with zero
  // Present records in the range. Trustworthy enough as a default since
  // the office working hours apply to everyone in the branch.
  const [officeShiftDuration, setOfficeShiftDuration] = useState<string>("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getOffice();
        const office = r?.offices?.[0];
        if (!office) return;
        const start = (office.working_hours_start || "").slice(0, 5);
        const end = (office.working_hours_end || "").slice(0, 5);
        if (!start || !end) return;
        const [sh, sm] = start.split(":").map(Number);
        const [eh, em] = end.split(":").map(Number);
        if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return;
        let mins = eh * 60 + em - (sh * 60 + sm);
        if (mins < 0) mins += 24 * 60; // overnight shifts
        const hh = String(Math.floor(mins / 60)).padStart(2, "0");
        const mm = String(mins % 60).padStart(2, "0");
        if (!cancelled) setOfficeShiftDuration(`${hh}:${mm}`);
      } catch {
        /* non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Resolve "my row" from today's status list so the employee card can
  // mirror what the admin sees for them. user_id comes from the decoded
  // JWT (set by AuthContext.loginSelect).
  const myTodayStatus: AttendanceStatus | null = (() => {
    const uid = (user as any)?.user_id || (user as any)?.id;
    if (!uid) return null;
    return employeeStatus.find((e) => e.user_id === uid) || null;
  })();
  const hasClockedIn = !!(
    myTodayStatus?.clock_in_time && myTodayStatus.clock_in_time !== "00:00:00"
  );
  const hasClockedOut = !!(
    myTodayStatus?.out_time && myTodayStatus.out_time !== "00:00:00"
  );

  // ── Remote-only: live "am I currently clocked in?" state ──────────────
  // Each clock-in and clock-out is its own AttendanceRecord row (the
  // /attendance endpoint never updates in place), so the current session
  // state is simply: more in-rows than out-rows today => clocked in.
  // null = not resolved yet (buttons disabled until the first fetch).
  const [remoteClockedIn, setRemoteClockedIn] = useState<boolean | null>(null);

  const refreshRemoteClockState = useCallback(async () => {
    const uid = (user as any)?.user_id || (user as any)?.id;
    if (!uid) return;
    const rows = await getAttendanceByDate(todayStr);
    const mine = rows.filter((r: any) => r.user_id === uid);
    const inCount = mine.filter((r: any) => r.clock_in_time).length;
    const outCount = mine.filter((r: any) => r.clock_out_time).length;
    setRemoteClockedIn(inCount > outCount);
  }, [user, todayStr]);

  useEffect(() => {
    if (showSessionClock) refreshRemoteClockState();
  }, [showSessionClock, refreshRemoteClockState]);

  const handleWebClock = async (clockIn: boolean) => {
    setWebClockBusy(clockIn ? "in" : "out");
    try {
      await sendWebClockEvent(clockIn, { workfromhome: myWorkfromhome, overtime: isOvertimeActive });
      if (showSessionClock) await refreshRemoteClockState();
      toast({
        title: clockIn ? "Clocked in" : "Clocked out",
        description: clockIn
          ? "You're marked present for today."
          : "Have a good rest of your day.",
      });
      await fetchData(startDate, endDate);
    } catch (err: any) {
      toast({
        title: clockIn ? "Couldn't clock in" : "Couldn't clock out",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setWebClockBusy(null);
    }
  };

  const openHoursModal = (
    emp: AttendanceStatus,
    mode: 'mark-present' | 'edit' = 'edit',
    options?: { date?: string; inTime?: string; outTime?: string },
  ) => {
    setHoursTarget(emp);
    setHoursModalMode(mode);
    const today = localToday();
    setHoursDateValue(options?.date || today);
    if (mode === 'edit') {
      setHoursInValue(
        (options?.inTime ?? emp.clock_in_time ?? "").slice(0, 5),
      );
      setHoursOutValue(
        (options?.outTime ?? emp.out_time ?? "").slice(0, 5),
      );
    } else {
      setHoursInValue(options?.inTime ? options.inTime.slice(0, 5) : "");
      setHoursOutValue(options?.outTime ? options.outTime.slice(0, 5) : "");
    }
    setHoursModalOpen(true);
  };

  const handleMarkPresent = (emp: AttendanceStatus) => {
    if (!emp?.user_id) {
      toast({
        description: "Couldn't identify this employee. Refresh and try again.",
        variant: "destructive",
      });
      return;
    }
    openHoursModal(emp, 'mark-present');
  };

  const [markingAbsentDayFor, setMarkingAbsentDayFor] = useState<string | null>(
    null,
  );
  const handleDayMarkAbsent = async (date: string) => {
    const target = selectedEmployeeStatus;
    if (!target?.user_id) {
      toast({
        description: "Couldn't identify this employee. Refresh and try again.",
        variant: "destructive",
      });
      return;
    }
    setMarkingAbsentDayFor(date);
    try {
      await adminMarkAbsent({
        user_id: target.user_id,
        attendance_date: date,
      });
      toast({
        title: "Marked absent",
        description: `${target.name} is absent on ${date}.`,
      });
      await fetchData(startDate, endDate);
      try {
        const refreshed = await getAttendanceDayWise(
          target.user_id,
          startDate,
          endDate,
        );
        setAttendanceWorkingHours(refreshed);
      } catch {
        /* non-fatal — table refreshes on next reopen */
      }
    } catch (err: any) {
      toast({
        title: "Couldn't mark absent",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setMarkingAbsentDayFor(null);
    }
  };

  const handleMarkAbsent = async (emp: AttendanceStatus) => {
    if (!emp?.user_id) {
      toast({
        description: "Couldn't identify this employee. Refresh and try again.",
        variant: "destructive",
      });
      return;
    }
    setMarkingAbsentFor(emp.user_id);
    try {
      const targetDate = localToday();
      await adminMarkAbsent({
        user_id: emp.user_id,
        attendance_date: targetDate,
      });
      toast({ title: "Marked absent", description: `${emp.name} is now absent.` });
      // Drop any pending-finalize flag so the Mark Present button is
      // re-eligible if the admin wants to flip back.
      setPendingMarkPresent((prev) => {
        if (!prev.has(emp.user_id!)) return prev;
        const next = new Set(prev);
        next.delete(emp.user_id!);
        return next;
      });
      await fetchData(startDate, endDate);
    } catch (err: any) {
      toast({
        title: "Couldn't mark absent",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setMarkingAbsentFor(null);
    }
  };

  const submitHoursOverride = async () => {
    if (!hoursTarget?.user_id) return;
    if (!hoursInValue) {
      toast({
        description: "Please enter a clock-in time.",
        variant: "destructive",
      });
      return;
    }
    if (!hoursDateValue) {
      toast({
        description: "Please pick an attendance date.",
        variant: "destructive",
      });
      return;
    }
    // Clock-out is optional. When provided, enforce a basic in < out
    // sanity check (HH:MM string compare is fine because both come from
    // the same input type and same date).
    if (hoursOutValue && hoursOutValue <= hoursInValue) {
      toast({
        description: "Clock-out time must be after clock-in time.",
        variant: "destructive",
      });
      return;
    }
    const targetDate = hoursDateValue;
    setHoursSubmitting(true);
    try {
      // Step 1: write the clock in (and clock out, if provided) onto
      // the chosen date.
      await adminUpdateWorkingHours({
        user_id: hoursTarget.user_id,
        attendance_date: targetDate,
        clock_in_time: hoursInValue,
        ...(hoursOutValue ? { clock_out_time: hoursOutValue } : {}),
      });
      // Step 2: flip status to Present for that date. Past dates are
      // supported by the backend — the admin overrides endpoint accepts
      // any YYYY-MM-DD.
      await adminMarkPresent({
        user_id: hoursTarget.user_id,
        attendance_date: targetDate,
      });
      toast({
        title: "Marked present",
        description: hoursOutValue
          ? `${hoursTarget.name} marked present (${hoursInValue} – ${hoursOutValue}) on ${targetDate}.`
          : `${hoursTarget.name} marked present at ${hoursInValue} on ${targetDate}.`,
      });

      setHoursModalOpen(false);
      setHoursTarget(null);
      await fetchData(startDate, endDate);
      // If the per-employee day-wise dialog is open, refresh it too so
      // the edited row reflects the new times immediately.
      if (statusDialogOpen && selectedEmployeeStatus?.user_id) {
        try {
          const refreshed = await getAttendanceDayWise(
            selectedEmployeeStatus.user_id,
            startDate,
            endDate,
          );
          setAttendanceWorkingHours(refreshed);
        } catch {
          /* non-fatal — the table will refresh on next open */
        }
      }
    } catch (err: any) {
      toast({
        title: "Couldn't mark present",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setHoursSubmitting(false);
    }
  };

  const fetchData = async (start: string, end: string) => {
    setLoading(true);
    try {
      // Fetch attendance summary for the selected range — drives the
      // pie chart / summary tiles.
      const summaryData = await getAttendanceSummary(start, end);
      setPieData(summaryData);
      // The status endpoint is single-date. When the user has narrowed
      // the range to a single day, snapshot that exact date so the table
      // matches the filter. For multi-day ranges fall back to today.
      const snapshotDate =
        start && end && start === end
          ? start
          : localToday();
      const statusData = await getAttendanceStatus(snapshotDate);
      setEmployeeStatus(statusData);
      setFilteredEmployeeStatus(statusData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchData(startDate, endDate);
    setTimeout(() => setIsRefreshing(false), 500);
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchData(startDate, endDate);
      // Switching dates wipes any "just edited" flags — those only apply
      // to the date the user was looking at when they saved.
      setPendingMarkPresent(new Set());
    }
  }, [startDate, endDate]);

  // Filter employees based on search and status
  useEffect(() => {
    let filtered = [...employeeStatus];

    if (searchQuery) {
      filtered = filtered.filter(emp =>
        emp.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(emp => emp.status === statusFilter);
    }

    setFilteredEmployeeStatus(filtered);
  }, [searchQuery, statusFilter, employeeStatus]);

  const handleEmployeeClick = async (emp: AttendanceStatus) => {
    setSelectedEmployeeStatus(emp);
    setAttendanceWorkingHours([]);
    setWorkingHoursError(null);
    setStatusDialogOpen(true);
    try {
      setIsLoadingWorkingHours(true);
      const workingHoursData = await getAttendanceDayWise(emp.user_id!, startDate, endDate);
      setAttendanceWorkingHours(workingHoursData);
    } catch (error: any) {
      console.error("Error fetching attendance working hours:", error);
      
      setWorkingHoursError(error?.message || "Couldn't load attendance details.");
    } finally {
      setIsLoadingWorkingHours(false);
    }
  };

  const getStatusIcon = (status: string) => {
    if (status === "Present") {
      return <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />;
    }
    if (status === "Leave") {
      return <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-amber-600" />;
    }
    return <XCircle className="w-3 h-3 sm:w-4 sm:h-4 text-red-600" />;
  };

  // Shared badge classes per status so Present / Leave / Absent read
  // consistently across the status table, the day-wise dialog and the
  // employee's own attendance list.
  //
  // Use SOLID `bg-{green,amber,red}-100` (not a `bg-gradient-*` tint) on
  // purpose: the Badge's default variant adds `bg-primary`, and a solid
  // background utility is in the same property group, so tailwind-merge drops
  // `bg-primary`. A gradient is a different group (background-image), so it
  // would leave `bg-primary` in place — and the global `.gt-themed` layer
  // rewrites `.bg-primary` to white text via !important, which made the label
  // invisible (white-on-light-tint). With the solid tint the theme instead
  // applies its readable tint+text pairs (--gt-prio-* / --gt-primary-container).
  // The text-/border- utilities are an accessible fallback when un-themed.
  // gt-att-pill--* (defined in global-theme.css) gives all three the same
  // soft-tint + near-black-ink shade as the Present pill, each in its own hue.
  // The bg-*-100 / text-*-800 utilities stay as an un-themed fallback.
  const statusBadgeClass = (status: string) =>
    status === "Present"
      ? "gt-att-pill gt-att-pill--present bg-green-100 text-green-800 border border-green-200"
      : status === "Leave"
        ? "gt-att-pill gt-att-pill--leave bg-amber-100 text-amber-800 border border-amber-200"
        : "gt-att-pill gt-att-pill--absent bg-red-100 text-red-800 border border-red-200";

  // Calculate statistics. "Leave" is its own bucket now, so Absent counts
  // only genuinely-absent employees (not those on approved leave).
  const totalEmployees = employeeStatus.length;
  const presentCount = employeeStatus.filter(emp => emp.status === "Present").length;
  const leaveCount = employeeStatus.filter(emp => emp.status === "Leave").length;
  const absentCount = employeeStatus.filter(emp => emp.status === "Absent").length;
  const attendancePercentage = totalEmployees > 0 ? ((presentCount / totalEmployees) * 100).toFixed(1) : 0;

  useEffect(() => {
    if (isAdmin) return;
    const uid = (user as any)?.id || (user as any)?.user_id;
    if (!uid) return;
    let cancelled = false;
    setMyMonthLoading(true);
    // Follow the applied date-range filter so the employee's Attendance Details
    // list (and the month stats) reflect the selected range. Defaults to the
    // current month (startDate/endDate are seeded to month-start → today).
    getAttendanceDayWise(uid, startDate, endDate)
      .then((rows) => {
        if (!cancelled) setMyMonthHours(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setMyMonthHours([]);
      })
      .finally(() => {
        if (!cancelled) setMyMonthLoading(false);
      });
    return () => { cancelled = true; };
    // isRefreshing in the deps lets the Refresh button re-trigger the fetch.
  }, [user, isAdmin, isRefreshing, startDate, endDate]);

  // Sundays are the company's weekly off — they shouldn't appear in the
  // employee's day-wise list, nor count toward Present/Absent totals. Drop
  // them up front so both the stats and the table below are Sunday-free
  // (mirrors the Sunday skip in the admin per-employee dialog above).
  const isSundayDate = (raw?: string) => {
    const d = (raw || "").slice(0, 10);
    if (!d) return false;
    const dt = new Date(d + "T00:00:00");
    return !Number.isNaN(dt.getTime()) && dt.getDay() === 0;
  };
  const myMonthRows = myMonthHours.filter((r) => !isSundayDate(r.date));

  const myMonthStats = (() => {
    // Day-wise rows span the selected date range (one row per day, Sundays
    // excluded), so the row count is the number of working days in range.
    const totalDays = myMonthRows.length;
    const presentDays = myMonthRows.filter(
      (r) => r.In_time && r.In_time !== "00:00:00"
    ).length;
    const leaveDays = myMonthRows.filter(
      (r) => !(r.In_time && r.In_time !== "00:00:00") && r.on_leave
    ).length;
    const absentDays = Math.max(0, totalDays - presentDays - leaveDays);
    const presencePct =
      totalDays > 0 ? ((presentDays / totalDays) * 100).toFixed(1) : "0";
    return { totalDays, presentDays, leaveDays, absentDays, presencePct };
  })();

  // Employee Attendance Details list, with the Present/Absent status filter
  // applied (the date range is already handled by the fetch above).
  const myFilteredHours = statusFilter === "all"
    ? myMonthRows
    : myMonthRows.filter((r) => {
        const present = !!(r.In_time && r.In_time !== "00:00:00");
        if (statusFilter === "Present") return present;
        if (statusFilter === "Leave") return !present && !!r.on_leave;
        // "Absent" excludes approved-leave days.
        return !present && !r.on_leave;
      });

  const handleStartDateChange = (date: string) => {
    setDraftStartDate(date);
    if (date > draftEndDate) {
      setDraftEndDate(date);
    }
  };

  const handleEndDateChange = (date: string) => {
    if (date >= draftStartDate) {
      setDraftEndDate(date);
    }
  };

  const applyFilters = () => {
    setSearchQuery(draftSearchQuery);
    setStatusFilter(draftStatusFilter);
    setStartDate(draftStartDate);
    setEndDate(draftEndDate);
    // Surface the snapshot date whenever the applied range collapses to
    // a single day — the table below will reflect that exact date rather
    // than today, and the popup explains the shift.
    if (draftStartDate && draftEndDate && draftStartDate === draftEndDate) {
      setShowInfoPopup(true);
    }
  };

  const clearAllFilters = () => {
    // Reset to the default range = 1st of this month → today (matching
    // the initial state above).
    const today = localToday();
    const monthStart = today.slice(0, 8) + "01";
    setSearchQuery("");
    setStatusFilter("all");
    setStartDate(monthStart);
    setEndDate(today);
    setDraftSearchQuery("");
    setDraftStatusFilter("all");
    setDraftStartDate(monthStart);
    setDraftEndDate(today);
  };

  const hasPendingFilterChanges =
    draftSearchQuery !== searchQuery ||
    draftStatusFilter !== statusFilter ||
    draftStartDate !== startDate ||
    draftEndDate !== endDate;

  const isFilterActive = (() => {
    const today = localToday();
    const monthStart = today.slice(0, 8) + "01";
    return (
      searchQuery !== "" ||
      statusFilter !== "all" ||
      startDate !== monthStart ||
      endDate !== today
    );
  })();

  // Day-wise rows shown inside the per-employee dialog: the API only
  // returns Present records, but the admin needs to see and act on every
  // workday in the range. We synthesise an Absent row for each missing
  // workday and skip Sundays (the company's weekly off — Saturdays are
  // still business days by default).
  const displayedDayRows: AttendanceWorkingHour[] = (() => {
    if (!startDate || !endDate) return attendanceWorkingHours;
    const byDate = new Map<string, AttendanceWorkingHour>();
    for (const r of attendanceWorkingHours) {
      const d = (r.date || "").slice(0, 10);
      if (d) byDate.set(d, r);
    }
    // The employee's shift_duration is the same across every workday in
    // this dialog (it's their assigned shift). Reuse whatever the API
    // sent on the first present row so synthetic Absent rows show the
    // same expected hours. When the API returned zero present rows we
    // fall back to the office-wide working hours so the column isn't
    // a sea of dashes for employees who haven't clocked in yet.
    const inheritedShift =
      attendanceWorkingHours.find(
        (r) => r.shift_duration && r.shift_duration !== "-",
      )?.shift_duration ||
      officeShiftDuration ||
      "-";
    // Anchor at local midnight to avoid DST / timezone drift when we
    // increment the cursor day by day.
    const start = new Date(startDate + "T00:00:00");
    const end = new Date(endDate + "T00:00:00");
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return attendanceWorkingHours;
    }
    const rows: AttendanceWorkingHour[] = [];
    const cursor = new Date(start);
    while (cursor <= end) {
      if (cursor.getDay() !== 0) {
        const y = cursor.getFullYear();
        const m = String(cursor.getMonth() + 1).padStart(2, "0");
        const d = String(cursor.getDate()).padStart(2, "0");
        const iso = `${y}-${m}-${d}`;
        const existing = byDate.get(iso);
        rows.push(
          existing ?? {
            date: iso,
            shift_duration: inheritedShift,
            In_time: "",
            out_time: "",
            total_working_hours: "00:00",
            total_working_hours_words: "0 minutes",
            total_hours: 0,
          },
        );
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return rows;
  })();

  // Summary derived from the merged rows so Absent counts reflect the
  // full date range (minus Sundays), not just the rows the API returned.
  const employeeSummary = (() => {
    if (displayedDayRows.length === 0) return null;
    const totalWorkingHours = displayedDayRows.reduce(
      (sum, record) => sum + (record.total_hours || 0),
      0,
    );
    const totalDays = displayedDayRows.length;
    const presentDays = displayedDayRows.filter(
      (r) => r.In_time && r.In_time !== "00:00:00",
    ).length;
    const leaveDays = displayedDayRows.filter(
      (r) => !(r.In_time && r.In_time !== "00:00:00") && r.on_leave,
    ).length;
    return {
      totalDays,
      presentDays,
      leaveDays,
      absentDays: totalDays - presentDays - leaveDays,
      totalWorkingHours: totalWorkingHours.toFixed(2),
      averageWorkingHours: (totalWorkingHours / totalDays).toFixed(2),
    };
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 sm:h-32 sm:w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="gt-shell min-h-screen">
      <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
        {/* Header Section */}
        <div className="gt-page-hero flex-col md:flex-row gap-4">
          <div>
            <h1 className="gt-page-title">Attendance <em>Management</em></h1>
            <p className="gt-page-sub" style={{ marginTop: 6 }}>
              Track and manage employee <em className="gt-serif-italic">attendance</em> records.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            {/* Today's Attendance + Clock In/Out — moved up beside the page
                header (employees only). */}
            {!isAdmin && (
              <Card className="bg-white border-0 shadow-md hover:shadow-lg transition-all overflow-hidden">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">
                        <MapPin className="w-3 h-3" /> Today&apos;s Attendance
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm">
                        <span className="flex items-center gap-1">
                          <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                          <span className="font-medium text-gray-700">In:</span>
                          <span className="font-mono">
                            {hasClockedIn ? myTodayStatus!.clock_in_time : "—"}
                          </span>
                        </span>
                        <span className="flex items-center gap-1">
                          <XCircle className="w-3.5 h-3.5 text-red-400" />
                          <span className="font-medium text-gray-700">Out:</span>
                          <span className="font-mono">
                            {hasClockedOut ? myTodayStatus!.out_time : "—"}
                          </span>
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {showSessionClock ? (
                        // ── Remote / Overtime: unlimited clock-in / clock-out ───
                        // Toggle purely on the live session state. Every press
                        // creates a fresh DB row server-side, so there's no
                        // once-per-day cap. Buttons disable until the first
                        // state fetch resolves (remoteClockedIn !== null).
                        remoteClockedIn ? (
                          <Button
                            onClick={() => handleWebClock(false)}
                            disabled={webClockBusy !== null || remoteClockedIn === null}
                            variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50 text-xs sm:text-sm"
                            title="Clock out"
                          >
                            <LogOut className="w-3.5 h-3.5 mr-1.5" />
                            {webClockBusy === "out" ? "Clocking out…" : "Clock Out"}
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handleWebClock(true)}
                            disabled={webClockBusy !== null || remoteClockedIn === null}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm"
                            title="Clock in"
                          >
                            <LogIn className="w-3.5 h-3.5 mr-1.5" />
                            {webClockBusy === "in" ? "Clocking in…" : "Clock In"}
                          </Button>
                        )
                      ) : (
                        // ── In-office / hybrid: original once-per-day flow (untouched) ──
                        <>
                          {!hasClockedIn && (
                            <Button
                              onClick={() => handleWebClock(true)}
                              disabled={webClockBusy !== null}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm"
                              title="Use your current location to clock in"
                            >
                              <LogIn className="w-3.5 h-3.5 mr-1.5" />
                              {webClockBusy === "in" ? "Clocking in…" : "Clock In"}
                            </Button>
                          )}
                          {hasClockedIn && !hasClockedOut && (
                            <Button
                              onClick={() => handleWebClock(false)}
                              disabled={webClockBusy !== null}
                              variant="outline"
                              className="border-red-200 text-red-600 hover:bg-red-50 text-xs sm:text-sm"
                              title="Use your current location to clock out"
                            >
                              <LogOut className="w-3.5 h-3.5 mr-1.5" />
                              {webClockBusy === "out" ? "Clocking out…" : "Clock Out"}
                            </Button>
                          )}
                          {hasClockedIn && hasClockedOut && (
                            <span className="text-xs sm:text-sm text-gray-500 px-2 py-1">
                              All done for today
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="gt-icon-btn sm:hidden"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </button>
              {/* <button
                onClick={handleRefresh}
                className="gt-icon-btn"
                disabled={isRefreshing}
                aria-label="Refresh"
              >
                <RefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button> */}
            </div>
          </div>
        </div>

        {isAdmin ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="gt-stat">
              <div className="gt-stat-label">
                <Users className="w-3.5 h-3.5" /> Total Employees
              </div>
              <p className="gt-stat-value">{totalEmployees}</p>
            </div>
            <div className="gt-stat">
              <div className="gt-stat-label">
                <CheckCircle className="w-3.5 h-3.5" /> Present Today
              </div>
              <p className="gt-stat-value" style={{ color: 'var(--gt-accent-strong)' }}>
                {presentCount}
              </p>
            </div>
            <div
              className="gt-stat"
              style={{
                borderColor:
                  absentCount > 0 ? 'rgba(220,38,38,0.18)' : undefined,
              }}
            >
              <div className="gt-stat-label">
                <XCircle className="w-3.5 h-3.5" /> Absent Today
              </div>
              <p
                className="gt-stat-value"
                style={{ color: absentCount > 0 ? '#991b1b' : undefined }}
              >
                {absentCount}
              </p>
              {leaveCount > 0 && (
                <p className="text-[11px] mt-0.5 font-medium text-amber-600 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> {leaveCount} on leave
                </p>
              )}
            </div>
            <div className="gt-stat">
              <div className="gt-stat-label">
                <Activity className="w-3.5 h-3.5" /> Attendance Rate
              </div>
              <p className="gt-stat-value">
                {attendancePercentage}
                <span style={{ fontSize: 18, marginLeft: 4, color: 'var(--gt-text-tertiary)' }}>
                  %
                </span>
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="gt-stat">
              <div className="gt-stat-label">
                <CheckCircle className="w-3.5 h-3.5" /> Days Present
                {myMonthLoading && (
                  <span className="text-[10px] text-gray-400 ml-1">loading…</span>
                )}
              </div>
              <p
                className="gt-stat-value"
                style={{ color: 'var(--gt-accent-strong)' }}
              >
                {myMonthStats.presentDays}
                <span
                  style={{
                    fontSize: 14,
                    marginLeft: 6,
                    color: 'var(--gt-text-tertiary)',
                  }}
                >
                  / {myMonthStats.totalDays}
                </span>
              </p>
            </div>
            <div
              className="gt-stat"
              style={{
                borderColor:
                  myMonthStats.absentDays > 0 ? 'rgba(220,38,38,0.18)' : undefined,
              }}
            >
              <div className="gt-stat-label">
                <XCircle className="w-3.5 h-3.5" /> Days Absent
              </div>
              <p
                className="gt-stat-value"
                style={{ color: myMonthStats.absentDays > 0 ? '#991b1b' : undefined }}
              >
                {myMonthStats.absentDays}
              </p>
            </div>
            <div className="gt-stat">
              <div className="gt-stat-label">
                <TrendingUp className="w-3.5 h-3.5" /> Month Presence
              </div>
              <p className="gt-stat-value">
                {myMonthStats.presencePct}
                <span style={{ fontSize: 18, marginLeft: 4, color: 'var(--gt-text-tertiary)' }}>
                  %
                </span>
              </p>
            </div>
          </div>
        )}

      {/* Filters — flat row of inputs (no Card wrapper). Each field uses
          its label text as a placeholder/title; for <input type="date">,
          which never displays a CSS placeholder, the `title` attr is used
          for accessibility while the input visibly shows the picked date.
          Apply Filters / Clear and the "N of M match" status row sit
          below, also without any card chrome. */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 sm:gap-4">
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 flex-1 ${
              isEmployee ? "lg:grid-cols-3" : "lg:grid-cols-4"
            }`}
          >
          {!isEmployee && (
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by employee name..."
                value={draftSearchQuery}
                onChange={(e) => setDraftSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && hasPendingFilterChanges) applyFilters();
                }}
                className="w-full pl-9 pr-3 h-10 border border-gray-200 rounded-lg bg-transparent text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          <input
            type="date"
            title="Start Date"
            placeholder="Start Date"
            value={draftStartDate}
            onChange={(e) => handleStartDateChange(e.target.value)}
            className="w-full px-3 h-10 border border-gray-200 rounded-lg bg-transparent text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />

          <input
            type="date"
            title="End Date"
            placeholder="End Date"
            value={draftEndDate}
            onChange={(e) => handleEndDateChange(e.target.value)}
            className="w-full px-3 h-10 border border-gray-200 rounded-lg bg-transparent text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />

          <select
            title="Status Filter"
            value={draftStatusFilter}
            onChange={(e) =>
              setDraftStatusFilter(e.target.value as "all" | "Present" | "Absent" | "Leave")
            }
            className="w-full px-3 h-10 border border-gray-200 rounded-lg bg-transparent text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All status</option>
            <option value="Present">Present</option>
            <option value="Absent">Absent</option>
            <option value="Leave">Leave</option>
          </select>
          </div>

          {/* Apply Filters sits in the same row as the inputs and is always
              visible; Clear only appears once a filter is active. */}
          <div className="flex items-center justify-end gap-2 shrink-0">
            {isFilterActive && (
              <button
                onClick={clearAllFilters}
                className="text-xs sm:text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1 px-1.5 sm:px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
              >
                <XCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden xs:inline">Clear</span>
              </button>
            )}
            <Button
              onClick={applyFilters}
              className="h-10 px-4 text-xs sm:text-sm bg-blue-600 hover:bg-blue-700 text-white"
            >
              Apply Filters
            </Button>
          </div>
        </div>

        {filteredEmployeeStatus.length !== employeeStatus.length && (
          <div className="mt-2 sm:mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm gap-2">
            <span className="text-blue-700 font-medium">
              <strong>{filteredEmployeeStatus.length}</strong> of <strong>{employeeStatus.length}</strong> employees match
            </span>
            <div className="flex flex-wrap gap-1.5 sm:gap-2 text-xs">
              {searchQuery && (
                <span className="text-blue-600 bg-blue-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs truncate max-w-[150px]">
                  🔍 {searchQuery}
                </span>
              )}
              {statusFilter !== "all" && (
                <span className="text-blue-600 bg-blue-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs">
                  📊 {statusFilter}
                </span>
              )}
              {(startDate !== localToday() || endDate !== localToday()) && (
                <span className="text-blue-600 bg-blue-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs">
                  📅 {startDate.substring(5)} → {endDate.substring(5)}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Employee Status Table (oversight roles only) - Collapsible with horizontal scroll for mobile */}
      {!isEmployee && (
      <Card className="bg-white border-0 shadow-md hover:shadow-lg transition-all mb-4 sm:mb-6 overflow-hidden">
        <div
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setIsStatusTableOpen(!isStatusTableOpen)}
        >
          <CardHeader className="pb-2 sm:pb-3 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="w-1 h-4 sm:h-6 bg-gradient-to-b from-purple-500 to-pink-600 rounded-full"></div>
                <CardTitle className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  <span className="hidden sm:inline">Employee Status</span>
                  <span className="sm:hidden">Status</span>
                </CardTitle>
                <CardDescription className="hidden sm:block text-gray-500">
                  {startDate && startDate === endDate && startDate !== localToday()
                    ? "Present / Absent status for the selected date — click a row for the full range"
                    : "Today's Present / Absent status — click a row for the full range"}
                </CardDescription>
              </div>
              <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">

                <div className="flex items-center gap-1 sm:gap-2 bg-gray-50 rounded-lg px-1.5 sm:px-3 py-0.5 sm:py-1.5">
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                  <span className="text-[10px] sm:text-sm text-gray-600">
                    {startDate && startDate === endDate && startDate !== localToday()
                      ? startDate.substring(5)
                      : `Today · ${localToday().substring(5)}`}
                  </span>
                </div>
                {isStatusTableOpen ? (
                  <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                )}
              </div>
            </div>
          </CardHeader>
        </div>

        {isStatusTableOpen && (
          <CardContent className="pt-3 sm:pt-4 animate-in slide-in-from-top-2 duration-200">
            <div className="overflow-auto max-h-[70vh] -mx-3 sm:mx-0">
              <div className="min-w-[280px] sm:min-w-full">
                <table className="w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg">
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">Employee</th>
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">In Time</th>
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">Out Time</th>
                      {canOverrideAttendance && (
                        <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-right text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredEmployeeStatus?.map((emp, idx) => {
                      const rowKey = emp.user_id || String(idx);
                      // Present but no settled out time => still clocked in.
                      const clockedIn = emp.status === "Present" && !emp.out_time;
                      return (
                      <Fragment key={rowKey}>
                      <tr className="hover:bg-gradient-to-r hover:from-gray-50 hover:to-transparent transition-colors group cursor-pointer" onClick={() => handleEmployeeClick(emp)}>
                        <td className="py-2 sm:py-3 px-2 sm:px-4">
                          <div className="flex items-center gap-1.5 sm:gap-3">
                            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                              <span className="text-[10px] sm:text-xs font-semibold text-gray-700">
                                {emp?.name?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="flex flex-col min-w-0">
                              <span className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors text-xs sm:text-sm truncate max-w-[100px] sm:max-w-none">
                                {emp?.name}
                              </span>
                              {emp?.employeeID && (
                                <span className="text-[9px] sm:text-[10px] text-gray-400 font-mono leading-tight truncate max-w-[100px] sm:max-w-[160px]">
                                  {emp.employeeID}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4">
                          <Badge
                            className={`${statusBadgeClass(emp.status)} font-semibold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full flex items-center gap-0.5 sm:gap-1 w-fit shadow-sm text-[10px] sm:text-xs`}
                            title={emp.status === "Leave" && emp.leave_type ? `On leave: ${emp.leave_type}` : undefined}
                          >
                            {getStatusIcon(emp.status)}
                            {emp.status === "Present"
                              ? "Present"
                              : emp.status === "Leave"
                                ? "Leave"
                                : "Absent"}
                          </Badge>
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-gray-600 font-mono text-[10px] sm:text-sm">
                          {emp?.clock_in_time || "-"}
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-gray-600 font-mono text-[10px] sm:text-sm">
                          {emp?.out_time ? (
                            <span>{emp.out_time}</span>
                          ) : clockedIn ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-600 px-2 py-0.5 text-[10px] font-sans font-semibold">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" /> active
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        {canOverrideAttendance && (
                          <td
                            className="py-2 sm:py-3 px-2 sm:px-4 text-right whitespace-nowrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {emp.status === "Leave" ? (
                              // On approved leave → no Present/Absent override
                              // from this table; the status is owned by the
                              // leave flow, so the action buttons are hidden.
                              <span className="text-[10px] sm:text-xs text-gray-400 italic">
                                On leave
                              </span>
                            ) : (
                            <div className="inline-flex gap-1.5">
                              {(() => {
                                const alreadyPresent = emp.status === "Present";
                                const inFlight = markingPresentFor === emp.user_id;
                                // Once present, the button morphs into
                                // an Edit action — reopens the modal
                                // pre-filled with today's recorded
                                // clock-in / clock-out so the admin can
                                // tweak either and save again.
                                const title = alreadyPresent
                                  ? "Edit today's clock-in / clock-out time"
                                  : "Set clock-in time and mark this employee present";
                                return (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 text-[10px] sm:text-xs"
                                    disabled={inFlight}
                                    onClick={() => {
                                      if (!emp?.user_id) {
                                        toast({
                                          description: "Couldn't identify this employee. Refresh and try again.",
                                          variant: "destructive",
                                        });
                                        return;
                                      }
                                      // Both paths go through the same
                                      // modal; the mode flips so the
                                      // dialog title / labels and the
                                      // prefill behaviour adapt.
                                      openHoursModal(
                                        emp,
                                        alreadyPresent ? 'edit' : 'mark-present',
                                      );
                                    }}
                                    title={title}
                                  >
                                    {inFlight
                                      ? "…"
                                      : alreadyPresent
                                        ? "Edit"
                                        : "Mark Present"}
                                  </Button>
                                );
                              })()}
                              
                              {(() => {
                                const alreadyAbsent = emp.status === "Absent";
                                const inFlight = markingAbsentFor === emp.user_id;
                                const disabled = alreadyAbsent || inFlight;
                                const title = alreadyAbsent
                                  ? "Already marked absent"
                                  : "Mark this employee as absent for the selected date";
                                return (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 text-[10px] sm:text-xs text-red-600 border-red-200 hover:bg-red-50"
                                    disabled={disabled}
                                    onClick={() => handleMarkAbsent(emp)}
                                    title={title}
                                  >
                                    {inFlight ? "…" : "Mark Absent"}
                                  </Button>
                                );
                              })()}

                            </div>
                            )}
                          </td>
                        )}
                      </tr>
                      </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filteredEmployeeStatus.length === 0 && (
                <div className="text-center py-6 sm:py-8 text-gray-500 text-xs sm:text-sm">
                  No employees found matching your filters
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>
      )}

      {/* Attendance Details — the logged-in employee's day-wise attendance for
          the current month, shown inline (replaces the today-only Employee
          Status table for employees). */}
      {isEmployee && (
        <Card className="bg-white border-0 shadow-md hover:shadow-lg transition-all mb-4 sm:mb-6 overflow-hidden">
          <CardHeader className="pb-2 sm:pb-3 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="w-1 h-4 sm:h-6 bg-gradient-to-b from-purple-500 to-pink-600 rounded-full"></div>
                <CardTitle className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <User className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  Attendance Details
                </CardTitle>
                <CardDescription className="hidden sm:block text-gray-500">
                  Your day-wise attendance for this month.
                </CardDescription>
              </div>
              {myMonthHours.length > 0 && (
                <div className="flex flex-wrap gap-1.5 sm:gap-2 text-[10px] sm:text-xs">
                  <span className="bg-green-100 text-green-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">✅ Present: {myMonthStats.presentDays}</span>
                  {myMonthStats.leaveDays > 0 && (
                    <span className="bg-amber-100 text-amber-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">🌴 Leave: {myMonthStats.leaveDays}</span>
                  )}
                  <span className="bg-red-100 text-red-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">❌ Absent: {myMonthStats.absentDays}</span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-3 sm:pt-4">
            {myMonthLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-xs sm:text-sm text-gray-600">Loading attendance data...</span>
              </div>
            ) : myFilteredHours.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                <p className="text-xs sm:text-sm">No attendance records match the selected filters</p>
              </div>
            ) : (
              <div className="overflow-auto max-h-[70vh] -mx-3 sm:mx-0">
                <div className="min-w-[420px] sm:min-w-full">
                  <table className="w-full text-xs sm:text-sm">
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <tr>
                        <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Date</th>
                        <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">In Time</th>
                        <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Out Time</th>
                        <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Total Hours</th>
                        <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {myFilteredHours.map((record, idx) => {
                        const isPresent = record.In_time && record.In_time !== "00:00:00";
                        const rowStatus = isPresent
                          ? "Present"
                          : record.on_leave
                            ? "Leave"
                            : "Absent";
                        // Same per-day punch breakdown the admin view gets: a
                        // day with >1 real clock-in/out pair (overtime / remote)
                        // becomes a clickable row that opens the SAME shared
                        // punches popup. Data-driven, so a past overtime day
                        // keeps its punches even after overtime is turned off.
                        const sessions = realSessions(record.sessions);
                        const sessionCount = sessions.length;
                        const hasMultiSessions = sessionCount > 1;
                        // Still clocked in → last punch is an open session; show
                        // "active" instead of the previous completed clock-out.
                        const lastSession = sessions[sessionCount - 1];
                        const isActiveSession = !!(lastSession && lastSession.in_time && !lastSession.out_time);
                        return (
                          <tr
                            key={idx}
                            className={`transition-colors ${hasMultiSessions ? 'cursor-pointer hover:bg-green-50' : 'hover:bg-gray-50'}`}
                            onClick={hasMultiSessions ? () => { setSessionsRecord(record); setSessionsDialogOpen(true); } : undefined}
                          >
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-900 text-[10px] sm:text-xs whitespace-nowrap">
                              <div className="flex flex-col">
                                <span>
                                  {new Date(record.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                                {hasMultiSessions && (
                                  <span className="mt-0.5 inline-flex items-center gap-1 text-[9px] sm:text-[10px] text-green-700 font-semibold">
                                    <Eye className="w-2.5 h-2.5" />
                                    {sessionCount} punches
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4">
                              <span className={`font-mono text-[10px] sm:text-xs ${isPresent ? 'text-green-600' : 'text-gray-400'}`}>
                                {isPresent ? record.In_time : "-"}
                              </span>
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4">
                              {isActiveSession ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-600 px-2 py-0.5 text-[10px] font-sans font-semibold">
                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" /> active
                                </span>
                              ) : (
                                <span className="font-mono text-[10px] sm:text-xs text-gray-600">
                                  {settledOutTime(record) || "-"}
                                </span>
                              )}
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4">
                              <span className="font-semibold text-blue-600 text-[10px] sm:text-xs">{record.total_working_hours}</span>
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4">
                              <Badge
                                className={`${statusBadgeClass(rowStatus)} font-semibold px-1.5 sm:px-3 py-0.5 sm:py-1 rounded-full w-fit shadow-sm text-[9px] sm:text-xs`}
                                title={rowStatus === "Leave" && record.leave_type ? `On leave: ${record.leave_type}` : undefined}
                              >
                                {rowStatus}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Logs Dialog - Responsive */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-[90%] sm:max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
              Attendance Details
            </DialogTitle>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">{selectedRecord?.name} - {selectedRecord?.date}</p>
          </DialogHeader>
          <div className="space-y-2 sm:space-y-3 mt-3 sm:mt-4">
            {selectedRecord ? (
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-3">
                {selectedRecord.in_time && (
                  <div className="flex justify-between items-center py-1.5 sm:py-2 border-b border-gray-200">
                    <span className="text-xs sm:text-sm text-gray-600">Check In</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-900">{selectedRecord.in_time}</span>
                  </div>
                )}
                {selectedRecord.additional_out_time && (
                  <div className="flex justify-between items-center py-1.5 sm:py-2 border-b border-gray-200">
                    <span className="text-xs sm:text-sm text-gray-600">Additional Out</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-900">{selectedRecord.additional_out_time}</span>
                  </div>
                )}
                {selectedRecord.additional_in_time && (
                  <div className="flex justify-between items-center py-1.5 sm:py-2 border-b border-gray-200">
                    <span className="text-xs sm:text-sm text-gray-600">Additional In</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-900">{selectedRecord.additional_in_time}</span>
                  </div>
                )}
                {selectedRecord.end_time && (
                  <div className="flex justify-between items-center py-1.5 sm:py-2 border-b border-gray-200">
                    <span className="text-xs sm:text-sm text-gray-600">Final Out</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-900">{selectedRecord.end_time}</span>
                  </div>
                )}
                {selectedRecord.total_time && (
                  <div className="flex justify-between items-center pt-1.5 sm:pt-2">
                    <span className="text-xs sm:text-sm font-semibold text-gray-700">Total Time</span>
                    <span className="text-base sm:text-lg font-bold text-blue-600">{selectedRecord.total_time}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6 sm:py-8 text-gray-500 text-xs sm:text-sm">No logs available</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Remote per-day punches — every clock-in / clock-out for the day */}
      <Dialog open={sessionsDialogOpen} onOpenChange={setSessionsDialogOpen}>
        <DialogContent className="max-w-[90%] sm:max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
              Clock-in / Clock-out
            </DialogTitle>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              {sessionsRecord
                ? new Date(sessionsRecord.date).toLocaleDateString('en-US', {
                    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
                  })
                : ""}
              {sessionsRecord
                ? ` · ${realSessions(sessionsRecord.sessions).length} punches`
                : ""}
              {sessionsRecord?.total_working_hours
                ? ` · ${sessionsRecord.total_working_hours} worked`
                : ""}
            </p>
          </DialogHeader>
          {sessionsRecord && realSessions(sessionsRecord.sessions).length > 0 ? (
            <div className="mt-3 sm:mt-4 rounded-xl border border-gray-200 overflow-hidden">
              {/* Column headers — mirror the outer table's In Time / Out Time */}
              <div className="grid grid-cols-[2rem_1fr_1fr] items-center gap-2 bg-gray-100 px-3 py-2 sm:px-4 text-[9px] sm:text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                <span>#</span>
                <span className="flex items-center gap-1">
                  <LogIn className="w-3 h-3 text-green-600" /> In Time
                </span>
                <span className="flex items-center gap-1">
                  <LogOut className="w-3 h-3 text-red-500" /> Out Time
                </span>
              </div>
              {/* Rows */}
              <div className="max-h-[55vh] overflow-y-auto divide-y divide-gray-100">
                {realSessions(sessionsRecord.sessions).map((s, i) => {
                  const active = !s.out_time && !!s.in_time; // open session
                  return (
                    <div
                      key={i}
                      className={`grid grid-cols-[2rem_1fr_1fr] items-center gap-2 px-3 py-2.5 sm:px-4 text-xs sm:text-sm transition-colors ${
                        active ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-green-50"
                      }`}
                    >
                      <span className="font-semibold text-gray-400">{i + 1}</span>
                      <span className="font-mono text-green-700">
                        {s.in_time || "—"}
                      </span>
                      <span className="font-mono text-red-600 flex items-center gap-2">
                        {s.out_time || (
                          active ? (
                            <span className="inline-flex items-center gap-1 text-amber-600 text-[10px] sm:text-xs font-semibold">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                              active
                            </span>
                          ) : (
                            "—"
                          )
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-6 sm:py-8 text-gray-500 text-xs sm:text-sm">
              No punches for this day
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Employee Attendance Working Hours Dialog - Responsive with horizontal scroll */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="max-w-[95%] sm:max-w-3xl md:max-w-4xl max-h-[85vh] overflow-y-auto rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
              <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              Attendance Details - {selectedEmployeeStatus?.name}
              {selectedEmployeeStatus?.employeeID && (
                <span className="text-xs sm:text-sm font-mono font-normal text-gray-400">
                  ({selectedEmployeeStatus.employeeID})
                </span>
              )}
            </DialogTitle>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-2">
              <p className="text-xs sm:text-sm text-gray-500">
                Date Range: {startDate} to {endDate}
              </p>
              {employeeSummary && (
                <div className="flex flex-wrap gap-1.5 sm:gap-2 text-[10px] sm:text-xs">
                  <span className="bg-green-100 text-green-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">✅ Present: {employeeSummary.presentDays}</span>
                  {employeeSummary.leaveDays > 0 && (
                    <span className="bg-amber-100 text-amber-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">🌴 Leave: {employeeSummary.leaveDays}</span>
                  )}
                  <span className="bg-red-100 text-red-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">❌ Absent: {employeeSummary.absentDays}</span>
                  <span className="bg-blue-100 text-blue-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">⏱️ Total: {employeeSummary.totalWorkingHours}h</span>
                </div>
              )}
            </div>
          </DialogHeader>
          
          {isLoadingWorkingHours ? (
            <div className="flex items-center justify-center py-8 sm:py-12">
              <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 sm:ml-3 text-xs sm:text-sm text-gray-600">Loading attendance data...</span>
            </div>
          ) : workingHoursError ? (
            
            <div className="text-center py-8 sm:py-12">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 rounded-full bg-red-50 flex items-center justify-center">
                <XCircle className="w-6 h-6 sm:w-8 sm:h-8 text-red-500" />
              </div>
              <p className="text-sm sm:text-base font-semibold text-red-600 mb-1">
                {workingHoursError}
              </p>
              <p className="text-[11px] sm:text-xs text-gray-500">
                Ask an admin to configure office hours for this branch
                so attendance details can be calculated.
              </p>
            </div>
          ) : displayedDayRows.length === 0 ? (
            <div className="text-center py-8 sm:py-12 text-gray-500">
              <Clock className="w-8 h-8 sm:w-12 sm:h-12 mx-auto text-gray-300 mb-2 sm:mb-3" />
              <p className="text-xs sm:text-sm">No attendance records found for the selected date range</p>
            </div>
          ) : (
            <div className="mt-3 sm:mt-4 overflow-auto max-h-[60vh] -mx-4 sm:mx-0">
              <div className="min-w-[500px] sm:min-w-full">
                <table className="w-full text-xs sm:text-sm">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0">
                    <tr>
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Date</th>
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Shift</th>
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">In Time</th>
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Out Time</th>
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Total Hours</th>
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Status</th>
                      {canOverrideAttendance && (
                        <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-right font-semibold text-gray-700 text-[10px] sm:text-xs">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {displayedDayRows.map((record, idx) => {
                      const isPresent = record.In_time && record.In_time !== "00:00:00";
                      const rowStatus = isPresent
                        ? "Present"
                        : record.on_leave
                          ? "Leave"
                          : "Absent";
                      // A day's punch breakdown is shown whenever it has more
                      // than one REAL clock-in/out pair. In-office employees do
                      // a single in/out cycle (1 session), so >1 punch
                      // effectively means an overtime / remote day. We gate on
                      // the punch DATA — NOT the live is_overtime / mode flags —
                      // so a PAST overtime day keeps showing its punches even
                      // after the employee's overtime is turned off (which
                      // clears the is_overtime flag + overtime dates).
                      const sessions = realSessions(record.sessions);
                      const sessionCount = sessions.length;
                      // Multi-punch day -> the row is clickable and opens a
                      // popup listing every clock-in / clock-out.
                      const hasMultiSessions = sessionCount > 1;
                      // Still clocked in → last punch is an open session; show
                      // "active" instead of the previous completed clock-out.
                      const lastSession = sessions[sessionCount - 1];
                      const isActiveSession = !!(lastSession && lastSession.in_time && !lastSession.out_time);
                      return (
                        <tr
                          key={idx}
                          className={`transition-colors ${hasMultiSessions ? 'cursor-pointer hover:bg-green-50' : 'hover:bg-gray-50'}`}
                          onClick={hasMultiSessions ? () => { setSessionsRecord(record); setSessionsDialogOpen(true); } : undefined}
                        >
                          <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-900 text-[10px] sm:text-xs whitespace-nowrap">
                            <div className="flex flex-col">
                              <span>
                                {new Date(record.date).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </span>
                              {hasMultiSessions && (
                                <span className="mt-0.5 inline-flex items-center gap-1 text-[9px] sm:text-[10px] text-green-700 font-semibold">
                                  <Eye className="w-2.5 h-2.5" />
                                  {sessionCount} punches
                                </span>
                              )}
                            </div>
                           </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4">
                            <div className="flex items-center gap-0.5 sm:gap-1">
                              <Briefcase className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-400" />
                              <span className="text-gray-600 text-[10px] sm:text-xs">{record.shift_duration}</span>
                            </div>
                           </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4">
                            <span className={`font-mono text-[10px] sm:text-xs ${isPresent ? 'text-green-600' : 'text-gray-400'}`}>
                              {record.In_time && record.In_time !== "00:00:00" ? record.In_time : "-"}
                            </span>
                           </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4">
                            {isActiveSession ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-600 px-2 py-0.5 text-[10px] font-sans font-semibold">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" /> active
                              </span>
                            ) : (
                              <span className="font-mono text-[10px] sm:text-xs text-gray-600">
                                {settledOutTime(record) || "-"}
                              </span>
                            )}
                           </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4">
                            <div className="flex flex-col">
                              <span className="font-semibold text-blue-600 text-[10px] sm:text-xs">{record.total_working_hours}</span>
                              <span className="text-[8px] sm:text-[10px] text-gray-400 hidden sm:inline">{record.total_working_hours_words}</span>
                            </div>
                           </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4">
                            <Badge
                              className={`${statusBadgeClass(rowStatus)} font-semibold px-1.5 sm:px-3 py-0.5 sm:py-1 rounded-full w-fit shadow-sm text-[9px] sm:text-xs`}
                              title={rowStatus === "Leave" && record.leave_type ? `On leave: ${record.leave_type}` : undefined}
                            >
                              {rowStatus}
                            </Badge>
                           </td>
                          {canOverrideAttendance && (
                            <td
                              className="py-2 sm:py-3 px-2 sm:px-4 text-right whitespace-nowrap"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {(() => {
                                const rowDate = (record.date || "").slice(0, 10);
                                const absentInFlight =
                                  markingAbsentDayFor === rowDate;
                                return (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-2 text-[10px] sm:text-xs"
                                        disabled={
                                          !selectedEmployeeStatus?.user_id ||
                                          absentInFlight
                                        }
                                      >
                                        {absentInFlight ? "…" : "Edit"}
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                      align="end"
                                      className="bg-white"
                                    >
                                      <DropdownMenuItem
                                        onClick={() => {
                                          if (!selectedEmployeeStatus) return;
                                          openHoursModal(
                                            selectedEmployeeStatus,
                                            isPresent ? 'edit' : 'mark-present',
                                            {
                                              date: rowDate,
                                              inTime: isPresent
                                                ? record.In_time
                                                : undefined,
                                              outTime: isPresent
                                                ? record.out_time
                                                : undefined,
                                            },
                                          );
                                        }}
                                      >
                                        Mark Present
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        className="text-red-600 focus:text-red-700"
                                        onClick={() =>
                                          handleDayMarkAbsent(rowDate)
                                        }
                                      >
                                        Mark Absent
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                );
                              })()}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={hoursModalOpen} onOpenChange={setHoursModalOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md sm:max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-semibold flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {hoursModalMode === 'mark-present' ? 'Mark Present' : 'Edit Attendance'}
            </DialogTitle>
          </DialogHeader>
          {hoursTarget && (
            <div className="space-y-3">
              <div className="text-xs text-gray-600">
                <strong>{hoursTarget.name}</strong> · {hoursDateValue}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="att-in">
                    Clock In <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="att-in"
                    type="time"
                    value={hoursInValue}
                    onChange={(e) => {
                      const v = e.target.value;
                      const prev = hoursInValue;
                      setHoursInValue(v);
                      if (
                        v.length === 5 &&
                        prev.length === 5 &&
                        v.split(":")[1] !== prev.split(":")[1]
                      ) {
                        setTimeout(() => e.target.blur(), 0);
                      }
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="att-out">
                    Clock Out{" "}
                    <span className="text-[10px] font-normal text-gray-400">
                      (optional)
                    </span>
                  </Label>
                  <Input
                    id="att-out"
                    type="time"
                    value={hoursOutValue}
                    onChange={(e) => {
                      const v = e.target.value;
                      const prev = hoursOutValue;
                      setHoursOutValue(v);
                      if (
                        v.length === 5 &&
                        prev.length === 5 &&
                        v.split(":")[1] !== prev.split(":")[1]
                      ) {
                        setTimeout(() => e.target.blur(), 0);
                      }
                    }}
                  />
                </div>
              </div>
              <p className="text-[11px] text-gray-500">
                {hoursModalMode === 'mark-present'
                  ? "Enter the clock-in time; the employee will be marked Present. Clock-out can be added now or filled in later."
                  : "Update the clock-in and (optionally) clock-out times for this date."}
              </p>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  className="w-full sm:w-auto"
                  disabled={hoursSubmitting}
                  onClick={() => setHoursModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="w-full sm:w-auto"
                  disabled={hoursSubmitting}
                  onClick={submitHoursOverride}
                >
                  {hoursSubmitting
                    ? "Saving…"
                    : hoursModalMode === 'mark-present'
                      ? "Mark Present"
                      : "Save"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Info Popup Dialog - Responsive */}
      <Dialog open={showInfoPopup} onOpenChange={setShowInfoPopup}>
        <DialogContent className="max-w-[90%] sm:max-w-md rounded-lg text-center">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Information</DialogTitle>
          </DialogHeader>
          <p className="text-xs sm:text-sm text-gray-600 mt-2">
            Below table contains data for{" "}
            {startDate && startDate === endDate
              ? startDate
              : "today's date"}
            .
            <br />
            To see full data click on employee name.
          </p>
          <div className="mt-3 sm:mt-4">
            <Button onClick={() => setShowInfoPopup(false)} className="text-xs sm:text-sm">OK</Button>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default Attendance;